import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Minus } from "lucide-react";
import { fmtNum } from "@/lib/seo";

const METRICS = [
  { key: "da", label: "Domain Authority (DA)", higherBetter: true, weight: 3 },
  { key: "pa", label: "Page Authority (PA)", higherBetter: true, weight: 3 },
  { key: "ss", label: "Spam Score (SS)", higherBetter: false, weight: 2 },
  { key: "backlinks", label: "Backlinks", higherBetter: true, weight: 1 },
  { key: "quality_bl", label: "Quality Backlinks", higherBetter: true, weight: 2 },
  { key: "ref_domains", label: "Referring Domains", higherBetter: true, weight: 2 },
  { key: "df_ref_domains", label: "DF Ref. Domains", higherBetter: true, weight: 2 },
  { key: "lp", label: "Link Propensity (LP)", higherBetter: false, weight: 1 },
];

const winner = (m, a, b) => {
  const va = a?.[m.key], vb = b?.[m.key];
  if (va == null || vb == null || va === vb) return 0;
  const aWins = m.higherBetter ? va > vb : va < vb;
  return aWins ? 1 : -1;
};

export const CompareModal = ({ open, onOpenChange, results }) => {
  const [domA, setDomA] = useState("");
  const [domB, setDomB] = useState("");
  const a = results.find((r) => r.domain === domA);
  const b = results.find((r) => r.domain === domB);

  const verdict = useMemo(() => {
    if (!a || !b || a.domain === b.domain) return null;
    let scoreA = 0, scoreB = 0;
    const reasons = [];
    METRICS.forEach((m) => {
      const w = winner(m, a, b);
      if (w === 1) scoreA += m.weight;
      if (w === -1) scoreB += m.weight;
    });
    if ((a.da ?? 0) !== (b.da ?? 0)) {
      const hi = (a.da ?? 0) > (b.da ?? 0) ? a : b;
      const lo = hi === a ? b : a;
      reasons.push(`${hi.domain} has stronger overall authority (DA ${hi.da} vs ${lo.da}), meaning search engines trust it more.`);
    }
    if ((a.ss ?? 0) !== (b.ss ?? 0)) {
      const clean = (a.ss ?? 100) < (b.ss ?? 100) ? a : b;
      const dirty = clean === a ? b : a;
      reasons.push(`${clean.domain} has a lower spam score (${clean.ss}% vs ${dirty.ss}%), which is safer for link building.`);
    }
    if ((a.ref_domains ?? 0) !== (b.ref_domains ?? 0)) {
      const hi = (a.ref_domains ?? 0) > (b.ref_domains ?? 0) ? a : b;
      const lo = hi === a ? b : a;
      reasons.push(`${hi.domain} has a wider link profile (${fmtNum(hi.ref_domains)} referring domains vs ${fmtNum(lo.ref_domains)}).`);
    }
    const qa = a.backlinks ? (a.quality_bl ?? 0) / a.backlinks : 0;
    const qb = b.backlinks ? (b.quality_bl ?? 0) / b.backlinks : 0;
    if (Math.abs(qa - qb) > 0.01) {
      const hi = qa > qb ? a : b;
      reasons.push(`${hi.domain} has a higher ratio of quality backlinks (${Math.round((hi === a ? qa : qb) * 100)}% of its link profile).`);
    }
    const overall = scoreA === scoreB ? null : scoreA > scoreB ? a : b;
    return { scoreA, scoreB, overall, reasons };
  }, [a, b]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="compare-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Compare Domains
          </DialogTitle>
          <DialogDescription>Pick two scanned domains to see a side-by-side metric breakdown and verdict.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-2">
          {[{ val: domA, set: setDomA, label: "Site A", tid: "compare-select-a" }, { val: domB, set: setDomB, label: "Site B", tid: "compare-select-b" }].map((s) => (
            <div key={s.label}>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{s.label}</p>
              <Select value={s.val} onValueChange={s.set}>
                <SelectTrigger data-testid={s.tid}>
                  <SelectValue placeholder={`Select ${s.label.toLowerCase()}…`} />
                </SelectTrigger>
                <SelectContent>
                  {results.map((r) => (
                    <SelectItem key={r.domain} value={r.domain}>{r.domain}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        {a && b && a.domain !== b.domain ? (
          <div className="mt-4" data-testid="compare-results">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Metric</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-gray-700">{a.domain}</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-gray-700">{b.domain}</th>
                </tr>
              </thead>
              <tbody>
                {METRICS.map((m) => {
                  const w = winner(m, a, b);
                  return (
                    <tr key={m.key} className="border-b border-gray-100">
                      <td className="py-2.5 text-gray-600">{m.label}</td>
                      <td className={`py-2.5 px-3 text-right ${w === 1 ? "font-bold text-green-700 bg-green-50" : "text-gray-700"}`}>
                        {fmtNum(a[m.key])}{w === 1 && <Trophy size={12} className="inline ml-1.5 -mt-0.5" />}
                      </td>
                      <td className={`py-2.5 px-3 text-right ${w === -1 ? "font-bold text-green-700 bg-green-50" : "text-gray-700"}`}>
                        {fmtNum(b[m.key])}{w === -1 && <Trophy size={12} className="inline ml-1.5 -mt-0.5" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {verdict && (
              <div className="mt-5 p-4 rounded-xl border border-[#C0392B]/20 bg-[#FADBD8]/30" data-testid="compare-verdict">
                <p className="font-bold text-gray-900 flex items-center gap-2">
                  {verdict.overall ? (
                    <><Trophy size={16} className="text-[#C0392B]" /> Winner: <span className="text-[#C0392B]">{verdict.overall.domain}</span> ({verdict.scoreA} – {verdict.scoreB})</>
                  ) : (
                    <><Minus size={16} /> It's a tie ({verdict.scoreA} – {verdict.scoreB})</>
                  )}
                </p>
                <ul className="mt-2 space-y-1.5 text-sm text-gray-700 list-disc pl-5">
                  {verdict.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8" data-testid="compare-placeholder">
            Select two different scanned domains to compare them side by side.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
